import {
    headingOffset,
    splitContentByHeadings
} from '@/lib/deepIntelligenceAvatar';
import {
    htmlArtifactsOf,
    isInlineRunElement,
    isMcpAppElement
} from '@/lib/deepIntelligenceRun';
import { ArrowLeft } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { useApi, useChatInteract, useConfig } from '@chainlit/react-client';
import type { IMessageElement, IThread } from '@chainlit/react-client';

import { Skeleton } from '@/components/ui/skeleton';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import { RunHeader } from '@/components/ScheduledAgents/RunHeader';
import { useAgentFallbackGuard } from '@/components/ScheduledAgents/useAgentFallbackGuard';
import ChatDisclaimer from '@/components/chat/ChatDisclaimer';
import MessageComposer from '@/components/chat/MessageComposer';
import { MessageContent } from '@/components/chat/Messages/Message/Content';
import { McpAppCard } from '@/components/chat/Messages/Message/McpAppCard';
import { Button } from '@/components/ui/button';

import { useDeepIntelligenceUnreadCount } from '@/hooks/DMuseDeepIntelligenceUnreadCount';
import { useIsBasicUser } from '@/hooks/useIsBasicUser';

import { IAttachment, attachmentsState } from 'state/chat';

// Newsreader serif font for editorial headlines only
function useNewsreaderFont() {
    useEffect(() => {
        if (document.getElementById('newsreader-font')) return;
        const link = document.createElement('link');
        link.id = 'newsreader-font';
        link.rel = 'stylesheet';
        link.href =
            'https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,600&display=swap';
        document.head.appendChild(link);
    }, []);
}

export default function Article() {
    const { briefId: threadId } = useParams<{ briefId: string }>();
    const navigate = useNavigate();
    const isBasicUser = useIsBasicUser();
    useNewsreaderFont();

    const { data: thread, isLoading: isThreadLoading } = useApi<IThread>(
        `/project/thread/${threadId}`
    );

    const { markOneRead } = useDeepIntelligenceUnreadCount();
    const hasMarkedReadForThreadRef = useRef<string | null>(null);
    useEffect(() => {
        if (!threadId || isThreadLoading || !thread) return;
        if (hasMarkedReadForThreadRef.current === threadId) return;
        hasMarkedReadForThreadRef.current = threadId;
        markOneRead(threadId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadId, thread, isThreadLoading]);

    // The follow-up composer at the bottom of this page autofocuses its
    // textarea on mount, which makes the browser scroll this container to
    // the bottom to bring it into view. Force it back to the top so readers
    // land on the article, not the composer.
    const containerRef = useRef<HTMLDivElement>(null);
    const hasScrolledForThreadRef = useRef(false);
    useEffect(() => {
        hasScrolledForThreadRef.current = false;
    }, [threadId]);
    useEffect(() => {
        if (isThreadLoading || hasScrolledForThreadRef.current) return;
        hasScrolledForThreadRef.current = true;
        requestAnimationFrame(() => {
            containerRef.current?.scrollTo({ top: 0 });
        });
    }, [threadId, isThreadLoading]);

    // A follow-up message here always forks this analysis thread into a new
    // working copy owned by the current user first — it never appends to the
    // canonical scheduled-job thread. Mirrors SharedThread.tsx's handleFork /
    // handleMessageSubmit pattern: fork, stash the message, navigate to the
    // new thread, and let the normal /thread/:id page's pending-message
    // auto-send effect (in MessageComposer) deliver it once resumed.
    const { config } = useConfig();
    const { uploadFile } = useChatInteract();
    const [isForking, setIsForking] = useState(false);
    const hasForkStartedRef = useRef(false);

    const handleFork = useCallback(async (): Promise<string | null> => {
        if (hasForkStartedRef.current) return null;
        hasForkStartedRef.current = true;
        setIsForking(true);

        try {
            const response = await fetch(
                `${window.location.origin}/project/thread/${threadId}/fork-analysis`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            if (!response.ok) {
                throw new Error('Failed to fork thread');
            }
            const data = await response.json();
            return data.threadId;
        } catch (err) {
            toast.error('Failed to continue this analysis');
            hasForkStartedRef.current = false;
            setIsForking(false);
            return null;
        }
    }, [threadId]);

    const handleMessageSubmit = useCallback(
        async (
            message: string,
            attachments: IAttachment[],
            commandId?: string
        ): Promise<string | null> => {
            if (isForking || hasForkStartedRef.current) return null;
            const newThreadId = await handleFork();
            if (newThreadId) {
                sessionStorage.setItem(
                    'pendingMessage',
                    JSON.stringify({
                        value: message,
                        attachments,
                        commandId,
                        threadId: newThreadId
                    })
                );
                window.dispatchEvent(
                    new CustomEvent('thread-forked-message-sent')
                );
                navigate(`/thread/${newThreadId}`);
            }
            return newThreadId;
        },
        [handleFork, isForking, navigate]
    );

    const { pinnedAgentName, guardedSubmit, dialog: agentFallbackDialog } =
        useAgentFallbackGuard({
            originalAgentId: thread?.metadata?.managed_agent_id,
            originalAgentName: thread?.metadata?.managed_agent_display_name,
            submit: handleMessageSubmit
        });

    const autoScrollRef = useRef(true);
    const setAttachments = useSetRecoilState(attachmentsState);
    const uploadFileRef = useRef(uploadFile);
    useEffect(() => {
        uploadFileRef.current = uploadFile;
    }, [uploadFile]);

    const fileSpec = useMemo(
        () => ({
            max_size_mb:
                config?.features?.spontaneous_file_upload?.max_size_mb || 500,
            max_files:
                config?.features?.spontaneous_file_upload?.max_files || 20,
            accept: config?.features?.spontaneous_file_upload?.accept || {
                'application/*': [],
                'audio/*': [],
                'image/*': [],
                'text/*': [],
                'video/*': []
            }
        }),
        [config]
    );

    const onFileUpload = useCallback((payloads: File[]) => {
        const attachements: IAttachment[] = payloads.map((file) => {
            const id = uuidv4();
            const { xhr, promise } = uploadFileRef.current(file, (progress) => {
                setAttachments((prev) =>
                    prev.map((a) =>
                        a.id === id ? { ...a, uploadProgress: progress } : a
                    )
                );
            });

            promise
                .then((res) => {
                    setAttachments((prev) =>
                        prev.map((a) =>
                            a.id === id
                                ? {
                                      ...a,
                                      serverId: res.id,
                                      uploaded: true,
                                      uploadProgress: 100,
                                      cancel: undefined
                                  }
                                : a
                        )
                    );
                })
                .catch((error) => {
                    toast.error(
                        `Failed to upload ${file.name}: ${
                            error?.message ?? error
                        }`
                    );
                    setAttachments((prev) => prev.filter((a) => a.id !== id));
                });

            return {
                id,
                type: file.type,
                name: file.name,
                size: file.size,
                uploadProgress: 0,
                cancel: () => {
                    xhr.abort();
                    setAttachments((prev) => prev.filter((a) => a.id !== id));
                },
                remove: () => {
                    setAttachments((prev) => prev.filter((a) => a.id !== id));
                }
            };
        });
        setAttachments((prev) => prev.concat(attachements));
    }, []);

    const onFileUploadError = useCallback(
        (error: string) => toast.error(error),
        []
    );

    const assistantSteps = (thread?.steps ?? []).filter(
        (s) => s.type === 'assistant_message'
    );
    const lastStep = assistantSteps[assistantSteps.length - 1];
    const content = lastStep?.output ?? '';
    // "mcpApp" elements are MCP-UI iframe apps — they need McpAppCard (which
    // fetches the UI resource live from the MCP server), not the generic
    // custom-element renderer (which expects a public/elements/<name>.jsx
    // file on disk and 404s for them).
    // Deep Intelligence stores every element's forId as the wrapping "run"
    // step's id (lastStep.parentId), not the assistant_message step's own id
    // — same convention mcpAppElements below relies on. MessageContent's
    // prepareContent, however, only inlines elements whose forId matches the
    // message object it's given (message.id) — so without remapping, these
    // elements are silently dropped (they never appear in inlinedElements),
    // even though they're correctly saved.
    const messageElements = (thread?.elements ?? [])
        .filter(isInlineRunElement)
        .map((el) => ({ ...el, forId: lastStep?.id ?? el.forId }));
    const mcpAppElements = (thread?.elements ?? [])
        .filter(
            (el): el is IMessageElement =>
                isMcpAppElement(el) &&
                el.forId === lastStep?.parentId
        )
        .toSorted(
            (a, b) =>
                ((a as any).props?.order ?? 0) - ((b as any).props?.order ?? 0)
        );

    const contentSections = splitContentByHeadings(content);

    const artifacts = htmlArtifactsOf(thread?.elements ?? []);

    const backBar = (
        <div className="top-0 z-10 sticky bg-background border-b">
            <MobileNavTrigger />
            <div className="flex mx-auto px-6 py-3 max-w-[760px]">
                <Button
                    onClick={() => navigate('/scheduled-jobs')}
                    variant="ghost"
                    className="group justify-start -ml-3 px-3 w-fit text-foreground hover:text-foreground"
                >
                    <span className="relative w-4 h-4 overflow-hidden">
                        <ArrowLeft className="absolute inset-0 transition-transform group-hover:-translate-x-full duration-300 ease-out" />

                        <ArrowLeft className="absolute inset-0 transition-transform translate-x-full group-hover:translate-x-0 duration-300 ease-out" />
                    </span>
                    Back to feed
                </Button>
            </div>
        </div>
    );

    if (isThreadLoading && !thread) {
        return (
            <div
                className="bg-background w-full h-full overflow-y-auto [scrollbar-gutter:stable]"
                aria-busy="true"
                aria-label="Loading analysis"
            >
                {backBar}
                <div className="mx-auto px-6 py-8 max-w-[760px]">
                    <Skeleton className="w-24 h-4" />
                    <Skeleton className="mt-4 w-full h-9" />
                    <Skeleton className="mt-2 w-2/3 h-9" />
                    <Skeleton className="mt-5 w-40 h-4" />
                    <div className="mt-6 border-t" />
                    <Skeleton className="mt-6 w-28 h-3" />
                    <Skeleton className="mt-3 rounded-xl w-[340px] h-56" />
                    <div className="flex flex-col gap-3 mt-8">
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-11/12 h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-4/5 h-4" />
                    </div>
                    <Skeleton className="mt-8 w-1/2 h-6" />
                    <div className="flex flex-col gap-3 mt-4">
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-5/6 h-4" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="bg-background w-full h-full overflow-y-auto [scrollbar-gutter:stable]"
        >
            {backBar}

            <div className="mx-auto px-6 py-8 max-w-[760px]">
                <RunHeader thread={thread} artifacts={artifacts} />

                {/* Real content */}
                {content && lastStep ? (
                    (() => {
                        const offset = headingOffset(contentSections);
                        const renderApp = (app: IMessageElement) => (
                            <McpAppCard
                                key={app.id}
                                className="my-8"
                                mcpAppMetadata={{
                                    resourceUri: (app as any).props
                                        ?.resourceUri,
                                    serverUrl: (app as any).props?.serverUrl,
                                    toolName: (app as any).props?.toolName,
                                    toolResult: (app as any).props?.toolResult,
                                    csp: (app as any).props?.csp
                                }}
                            />
                        );
                        // MessageContent's SourceSummaryProvider only makes
                        // `sources` available to citation badges rendered
                        // WITHIN that same instance — so a citation that
                        // happens to fall in section 1+ (after the first
                        // heading) found nothing to look up when only section
                        // 0 got `messageElements`, even though the source
                        // data itself was captured and persisted correctly.
                        // "Sources used" isn't rendered as its own visual
                        // block (MessageContent filters it out of
                        // visibleElements — it's context-only), so handing it
                        // to every section is safe: no duplicate rendering,
                        // just data available wherever a badge needs it.
                        const sourcesOnlyElements = messageElements.filter(
                            (el) =>
                                el.name === 'Sources used' &&
                                el.type === 'custom'
                        );
                        // sectionIndex (deep_intelligence_consumer.py) is a
                        // REAL signal captured at generation time: how many
                        // "## " headings had appeared in the streamed text
                        // at the moment this chart was created. Convert that
                        // to a contentSections index and group every app by
                        // it — this correctly handles two charts landing
                        // under the same heading (both render there) and a
                        // chart generated after the last heading (clamped
                        // to the final section instead of needing an
                        // "order N ↔ heading N" position to exist for it).
                        // Older reports generated before this field existed
                        // fall back to the previous position-only pairing.
                        const hasSectionIndex = mcpAppElements.some(
                            (el) =>
                                typeof (el as any).props?.sectionIndex ===
                                'number'
                        );
                        const appsBySection = new Map<
                            number,
                            IMessageElement[]
                        >();
                        const addApp = (
                            index: number,
                            app: IMessageElement
                        ) => {
                            const clamped = Math.min(
                                Math.max(0, index),
                                Math.max(0, contentSections.length - 1)
                            );
                            const list = appsBySection.get(clamped) ?? [];
                            list.push(app);
                            appsBySection.set(clamped, list);
                        };
                        mcpAppElements.forEach((app, idx) => {
                            if (hasSectionIndex) {
                                const sectionIndex =
                                    (app as any).props?.sectionIndex ?? 0;
                                addApp(sectionIndex - 1 + offset, app);
                            } else {
                                addApp(idx + offset, app);
                            }
                        });
                        return (
                            <>
                                {contentSections.map((section, i) => {
                                    const appsForSection =
                                        appsBySection.get(i) ?? [];
                                    return (
                                        <Fragment key={i}>
                                            <MessageContent
                                                message={{
                                                    ...lastStep,
                                                    output: section
                                                }}
                                                elements={
                                                    i === 0
                                                        ? messageElements
                                                        : sourcesOnlyElements
                                                }
                                            />
                                            {appsForSection.map(renderApp)}
                                        </Fragment>
                                    );
                                })}
                            </>
                        );
                    })()
                ) : (
                    <p className="text-muted-foreground">
                        This run didn't produce any content.
                    </p>
                )}
            </div>

            {/* Follow-up composer — the real chat input. The first message
                forks this analysis into your own working thread. */}
            {!isBasicUser && (
                <div className="bottom-0 z-10 sticky bg-linear-to-t from-background from-75% to-transparent pt-14">
                    <div className="flex flex-col mx-auto px-6 max-w-[760px]">
                        <MessageComposer
                            fileSpec={fileSpec}
                            onFileUpload={onFileUpload}
                            onFileUploadError={onFileUploadError}
                            autoScrollRef={autoScrollRef}
                            onBeforeSubmit={guardedSubmit}
                            hideSuggestedPrompts
                            pinnedAgentName={pinnedAgentName}
                        />
                        {agentFallbackDialog}
                        <ChatDisclaimer />
                    </div>
                </div>
            )}
        </div>
    );
}
